import { EyeOutlined } from '@ant-design/icons';
import {
  Button,
  Form,
  Image,
  message,
  Modal,
  Spin,
  Upload,
  UploadFile,
  UploadProps,
} from 'antd';
import { useUploadImage } from 'features/Dashboard/use-cases/upload-image';
import { useCreateUploadLink } from 'features/document-report/use-cases/upload-documents-link';
import { useUserContext } from 'features/users';
import { Fragment, useState } from 'react';
import { useParams } from 'react-router-dom';
import { convertFileSize, validateFileBeforeUpload } from 'routes/utils';

export const UploadDocumentsItemLink = () => {
  const formName = 'add-user-form';
  const params = useParams();
  const { id, uniqueid } = params;
  const { companyUser } = useUserContext();
  const [form] = Form.useForm();
  const { mutate, isLoading } = useCreateUploadLink();
  const [fileList, setFileList] = useState<any[]>([]);
  const [previewImage, setPreviewImage] = useState<string>('');
  const [previewOpen, setPreviewOpen] = useState<boolean>(false);
  const [filenameUrl, setFileNameUrl] = useState('');
  const [isImageUploading, setIsImageUploading] = useState<boolean>(false); // Loading state for image upload
  const [blobUrl, setBlobUrl] = useState<string>(''); // Store the uploaded blob URL
  const { isLoading: imageLoading, mutate: uploadImage } = useUploadImage();
  const handlePreview = async (file: UploadFile) => {
    setPreviewImage(file.url || (file.preview as string));
    setPreviewOpen(true);
  };
  const handleImageUpload = (file: UploadFile) => {
    const formData = new FormData();
    formData.append('file', file as any);
    setIsImageUploading(true); // Set uploading state to true
    uploadImage(
      { id: id as string, formData }, // Pass id and file as payload
      {
        onSuccess: (response: any) => {
          setFileNameUrl(response.fileName);
          message.success('File uploaded successfully');
          setBlobUrl(response?.blobUrl);
          setIsImageUploading(false); // Reset uploading state
        },
        onError: () => {
          message.error('Image upload failed');
          setIsImageUploading(false); // Reset uploading state
        },
      },
    );
  };
  const getAllCompanyIds = () => {
    // Check if `companyUser` and `rc` array exist
    if (companyUser?.rc?.length > 0) {
      // Map through the `rc` array to extract all `CompanyId` values
      return companyUser.rc.map((item: any) => item.CompanyId);
    }

    return [];
  };
  const companyIds = getAllCompanyIds();
  const handleChange: UploadProps['onChange'] = ({ fileList: newFileList }) =>
    setFileList(newFileList);
  const handleUpdate = () => {
    const formData = new FormData();
    if (!fileList || fileList.length === 0) {
      message.error('Please upload at least one file.');

      return;
    }
    formData.append('CompanyId', id as any);
    formData.append('UniqueId', uniqueid as any);
    const uploadedFile = fileList[0]?.originFileObj;
    if (uploadedFile) {
      // Get the filename and filesize from the file object
      const fileSize = uploadedFile.size;
      // Append filename and filesize to FormData
      formData.append('FileName', filenameUrl);
      formData.append('FileSize', convertFileSize(fileSize)); // FileSize is typically a number, so convert it to string
    }
    mutate(formData, {
      onSuccess: (data: any) => {
        // Redirect after 1 second on successful API call
        if (data.rs == 0) {
          message.error(data?.rm);
        } else {
          message.success('Document Upload Successfully');
          form.resetFields();
          setFileList([]);
          // Optionally clear fileNames if used
          // setTimeout(() => {
          //   navigate('/login'); // Redirect to the login page
          // }, 1000); // 1000 ms = 1 second
        }
      },
    });
  };
  const beforeUpload = (file: any) => {
    const isValid = validateFileBeforeUpload(file);
    if (isValid) {
      handleImageUpload(file); // Call handleImageUpload only if valid
    }

    return isValid; // If false, upload will be prevented
  };

  return (
    <Fragment>
      {companyIds.includes(id) ? (
        <Form
          id={formName}
          name={formName}
          form={form}
          layout='vertical'
          onFinish={handleUpdate}

          // validateTrigger={validateTrigger}
          // disabled={isLoading}
        >
          <Form.Item
            name='files' // Use plural if multiple files
          >
            <div className='upld_item'>
              <div className='updcbx'>
                <Upload
                  action='https://660d2bd96ddfa2943b33731c.mockapi.io/api/upload'
                  listType='picture-card'
                  fileList={fileList}
                  onPreview={handlePreview}
                  onChange={handleChange}
                  // beforeUpload={validateFileType} // Custom validation for file type
                  multiple // Allow multiple files
                  maxCount={50} // Set maximum number of files if needed
                  accept='.png,.jpeg,.jpg,.webp,.csv,.xlsx,.xls,.pdf' // Allowed file types
                  beforeUpload={beforeUpload}
                >
                  {fileList?.length >= 1 ? null : (
                    <div className='upld_img'>
                      <img src='/upload-img.png' alt='Upload icon' />
                      <p className='drp_text'>
                        Drag and drop here <span>or</span>{' '}
                        <a href='javascript:void(0)'>Browse Files</a>
                      </p>
                    </div>
                  )}
                </Upload>
                {blobUrl && fileList.length > 0 && (
                  <EyeOutlined
                    className='eyeiconi'
                    onClick={() => setPreviewOpen(true)}
                  />
                )}
              </div>
              <Modal
                open={previewOpen}
                onCancel={() => setPreviewOpen(false)}
                footer={null}
                centered
                width={800}
              >
                {blobUrl.endsWith('.jpg') ||
                blobUrl.endsWith('.jpeg') ||
                blobUrl.endsWith('.png') ||
                blobUrl.endsWith('.webp') ? (
                  <Image src={blobUrl} alt='Preview' />
                ) : (
                  <iframe
                    src={`https://docs.google.com/gview?url=${blobUrl}&embedded=true`}
                    style={{ width: '100%', height: '600px', border: 'none' }}
                    title='Document Preview'
                  />
                )}
              </Modal>
              {isImageUploading && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    zIndex: 1,
                  }}
                >
                  <Spin size='large' />
                </div>
              )}
            </div>

            {/* Preview Logic */}
            {previewImage && (
              <Image
                wrapperStyle={{ display: 'none' }}
                preview={{
                  visible: previewOpen,
                  onVisibleChange: (visible) => setPreviewOpen(visible),
                  afterOpenChange: (visible) => !visible && setPreviewImage(''),
                }}
                src={previewImage}
              />
            )}
          </Form.Item>
          <Form.Item className='text-center'>
            <Button
              type='primary'
              htmlType='submit'
              disabled={isLoading || imageLoading}
            >
              Upload Documents
            </Button>
          </Form.Item>
        </Form>
      ) : (
        <div
          className='text-center'
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            fontSize: '16px',
          }}
        >
          No access permission
        </div>
      )}
    </Fragment>
  );
};
